import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
} from '@mui/material';
import {
  People,
  Security,
  Settings,
  Person,
  ArrowBack,
  Apps,
  Category,
  Group,
  LocationOn,
  Description,
  Receipt,
  Assessment,
  SwapHoriz,
  Settings as SettingsIcon,
  Warehouse,
  Report,
  AutoAwesome,
  ShoppingCart,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface SettingsSidebarProps {
  showBackButton?: boolean;
  backButtonText?: string;
  backButtonPath?: string;
  variant?: 'settings' | 'suppliers' | 'supplier_details' | 'stock';
  currentView?: 'suppliers' | 'supplier_types' | 'payment_types' | 'warehouse_stock' | 'orders_listing' | 'supplier_inquiry' | 'supplier_contract' | 'supplier_expenses' | 'supplier_sale_report' | 'create_batch' | 'returns' | 'process' | 'warehouse_received_qty' | 'reports' | 'items' | 'stocks' | 'categories';
  onViewChange?: (view: any) => void;
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  showBackButton = false,
  backButtonText = "Back",
  backButtonPath = "/users",
  variant = 'settings',
  currentView = 'suppliers',
  onViewChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const settingsMenuItems = [
    {
      text: 'Users Management',
      icon: <People />,
      path: '/users',
      active: location.pathname === '/users' || location.pathname === '/users/add' || location.pathname.startsWith('/users/edit/'),
    },
    {
      text: 'Teams Management',
      icon: <Group />,
      path: '/teams',
      active: location.pathname === '/teams' || location.pathname === '/teams/add' || location.pathname.startsWith('/teams/edit/'),
    },
    {
      text: 'Locations Management',
      icon: <LocationOn />,
      path: '/locations',
      active: location.pathname === '/locations' || location.pathname === '/locations/add' || location.pathname.startsWith('/locations/edit/'),
    },
    {
      text: 'Roles Management',
      icon: <Security />,
      path: '/roles',
      active: location.pathname === '/roles' || location.pathname === '/roles/add' || location.pathname.startsWith('/roles/edit/'),
    },
    {
      text: 'Modules Management',
      icon: <Apps />,
      path: '/modules',
      active: location.pathname === '/modules' || location.pathname === '/modules/add' || location.pathname.startsWith('/modules/edit/'),
    },
    {
      text: 'Profile',
      icon: <Person />,
      path: '/profile',
      active: location.pathname === '/profile',
    },
  ];

  const suppliersMenuItems = [
    {
      text: 'Suppliers',
      icon: <People />,
      path: '/suppliers',
      active: currentView === 'suppliers',
    },
    {
      text: 'Supplier Types',
      icon: <Category />,
      path: '/suppliers',
      active: currentView === 'supplier_types',
    },
    {
      text: 'Payment Types',
      icon: <Receipt />,
      path: '/suppliers',
      active: currentView === 'payment_types',
    },
  ];

  const supplierDetailsMenuItems = [
    {
      text: 'Warehouse Stock',
      icon: <Warehouse />,
      path: '/suppliers',
      active: currentView === 'warehouse_stock',
    },
    {
      text: 'Orders Listing',
      icon: <ShoppingCart />,
      path: '/suppliers',
      active: currentView === 'orders_listing',
    },
    {
      text: 'Supplier Inquiry',
      icon: <People />,
      path: '/suppliers',
      active: currentView === 'supplier_inquiry',
    },
    {
      text: 'Supplier Expenses',
      icon: <Receipt />,
      path: '/suppliers',
      active: currentView === 'supplier_expenses',
    },
    {
      text: 'Supplier Sale Report',
      icon: <Assessment />,
      path: '/suppliers',
      active: currentView === 'supplier_sale_report',
    },
    {
      text: 'Returns',
      icon: <SwapHoriz />,
      path: '/suppliers',
      active: currentView === 'returns',
    },
    {
      text: 'Process',
      icon: <SettingsIcon />,
      path: '/suppliers',
      active: currentView === 'process',
    },
    {
      text: 'Reports',
      icon: <Report />,
      path: '/suppliers',
      active: currentView === 'reports',
    },
  ];

  const stockMenuItems = [
    {
      text: 'Stock Management',
      icon: <Security />,
      path: '/stock',
      active: currentView === 'stocks',
    },
    {
      text: 'Items',
      icon: <People />,
      path: '/stock',
      active: currentView === 'items',
    },
    {
      text: 'Categories',
      icon: <Category />,
      path: '/categories',
      active: currentView === 'categories',
    },
  ];

  const menuItems = variant === 'suppliers' ? suppliersMenuItems : 
                   variant === 'supplier_details' ? supplierDetailsMenuItems :
                   variant === 'stock' ? stockMenuItems : settingsMenuItems;

  const handleMenuItemClick = (path: string, itemText: string) => {
    if (variant === 'suppliers' && onViewChange) {
      const viewMap: { [key: string]: string } = {
        'Suppliers': 'suppliers',
        'Supplier Types': 'supplier_types',
        'Payment Types': 'payment_types',
      };
      
      if (viewMap[itemText]) {
        onViewChange(viewMap[itemText]);
      }
    } else if (variant === 'supplier_details' && onViewChange) {
      const viewMap: { [key: string]: string } = {
        'Warehouse Stock': 'warehouse_stock',
        'Orders Listing': 'orders_listing',
        'Supplier Inquiry': 'supplier_inquiry',
        'Supplier Expenses': 'supplier_expenses',
        'Supplier Sale Report': 'supplier_sale_report',
        'Returns': 'returns',
        'Process': 'process',
        'Reports': 'reports',
      };
      
      if (viewMap[itemText]) {
        onViewChange(viewMap[itemText]);
      }
    } else if (variant === 'stock' && onViewChange) {
      if (itemText === 'Items') {
        onViewChange('items');
      } else if (itemText === 'Stock Management') {
        onViewChange('stocks');
      } else if (itemText === 'Categories') {
        navigate('/categories');
      }
    } else {
      navigate(path);
    }
  };

  const handleBackClick = () => {
    navigate(backButtonPath);
  };

  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: 'calc(100vh - 140px)' }}>
      <CardContent sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
          Sub Menu
        </Typography>
        <Box sx={{ 
          width: '100%', 
          height: '1px', 
          backgroundColor: '#e5e7eb',
          mb: 2 
        }} />
        
        {showBackButton && !location.pathname.startsWith('/users') && !location.pathname.startsWith('/teams') && !location.pathname.startsWith('/locations') && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleBackClick}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '8px',
                borderColor: '#e5e7eb',
                color: '#374151',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#d1d5db',
                  backgroundColor: '#f9fafb',
                },
              }}
            >
              {backButtonText}
            </Button>
          </Box>
        )}
        
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.text}
              onClick={() => handleMenuItemClick(item.path, item.text)}
              sx={{
                borderRadius: '8px',
                mb: 1,
                backgroundColor: item.active ? '#e0f2f1' : 'transparent',
                color: item.active ? '#0d9488' : '#6b7280',
                '&:hover': {
                  backgroundColor: item.active ? '#ccedea' : '#f9fafb',
                },
              }}
            >
              <ListItemIcon>
                {React.cloneElement(item.icon, { 
                  color: item.active ? '#0d9488' : '#6b7280' 
                })}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default SettingsSidebar;
